/**
 * Kurv persisteret pr. FIRMA i cart_items (kundevisning redigerer kundens
 * rigtige kurv). Optimistiske opdateringer med fejl-toast + genindlæsning
 * ved DB-fejl. Antalsregler (pack_size/min_order_qty) håndhæves via domain.
 *
 * FLERE NAVNGIVNE KURVE (à la store B2B-apps): cart_label på cart_items
 * skelner kurvene; "Standard" er standardkurven. Den aktive kurv huskes pr.
 * firma i localStorage på enheden. Badge/totaler/checkout følger altid den
 * aktive kurv.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase/client";
import { logger } from "@/lib/logger";
import { useAuth } from "@/features/auth/AuthContext";
import { useActiveCompanyId } from "@/features/sales/ImpersonationContext";
import {
  computeTotalWeightKg,
  defaultAddQuantity,
  incrementQuantity,
  lineTotalExVat,
  normalizeQuantity,
} from "@/domain";

export interface CartItem {
  variantId: string;
  productId: string;
  title: string;
  variantTitle?: string;
  sku?: string;
  quantity: number;
  /** Effektiv B2B-pris ex moms pr. stk. */
  price: number;
  /** Listepris ex moms pr. stk. (til besparelsesvisning). */
  originalPrice: number;
  image?: string;
  minOrderQty: number;
  packSize: number;
  weight?: number;
  weightUnit?: string;
  isAvailable?: boolean;
  inventoryQuantity?: number;
}

export type NewCartItem = Omit<CartItem, "quantity" | "minOrderQty" | "packSize"> & {
  minOrderQty?: number;
  packSize?: number;
};

/** Kendetegn for manuelle linjer (sælger): variant_id "custom-<uuid>". */
export const CUSTOM_VARIANT_PREFIX = "custom-";
export function isCustomCartItem(item: Pick<CartItem, "variantId">): boolean {
  return item.variantId.startsWith(CUSTOM_VARIANT_PREFIX);
}

/** Standardkurvens navn (kan ikke slettes). */
export const DEFAULT_CART = "Standard";

export interface CartSummary {
  label: string;
  /** Antal styk i kurven (sum af quantity). */
  quantity: number;
}

interface CartState {
  items: CartItem[];
  isLoading: boolean;
  totalItems: number;
  subtotalExVat: number;
  totalSavingsExVat: number;
  totalWeightKg: number;
  /** Navnet på den aktive kurv ("Standard" som udgangspunkt). */
  activeCart: string;
  /** Alle firmaets kurve med antal (inkl. den aktive, også hvis tom). */
  carts: CartSummary[];
  switchCart: (label: string) => void;
  /** Opret og skift til en ny, tom kurv. */
  createCart: (label: string) => void;
  /** Slet en kurv og dens varer. Standardkurven kan ikke slettes. */
  deleteCart: (label: string) => Promise<void>;
  addItem: (item: NewCartItem, quantity?: number) => void;
  addCustomItem: (input: { title: string; priceExVat: number; quantity: number; sku?: string }) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  /** Sælger-funktion: ret linjepris (skrives på kundens kurvrække). */
  updatePrice: (variantId: string, newPriceExVat: number) => void;
  clearCart: () => Promise<void>;
  reload: () => Promise<void>;
}

const CartContext = createContext<CartState | undefined>(undefined);

function activeCartStorageKey(companyId: string): string {
  return `lab3d-aktiv-kurv-${companyId}`;
}

function readActiveCart(companyId: string | null): string {
  if (!companyId) return DEFAULT_CART;
  try {
    return localStorage.getItem(activeCartStorageKey(companyId)) || DEFAULT_CART;
  } catch {
    return DEFAULT_CART;
  }
}

function rowToItem(row: {
  variant_id: string;
  product_id: string;
  title: string;
  variant_title: string | null;
  sku: string | null;
  quantity: number;
  price: number;
  original_price: number;
  image: string | null;
  min_order_qty: number | null;
  pack_size: number | null;
  weight: number | null;
  weight_unit: string | null;
  is_available: boolean | null;
  inventory_quantity: number | null;
}): CartItem {
  return {
    variantId: row.variant_id,
    productId: row.product_id,
    title: row.title,
    variantTitle: row.variant_title ?? undefined,
    sku: row.sku ?? undefined,
    quantity: row.quantity,
    price: Number(row.price),
    originalPrice: Number(row.original_price),
    image: row.image ?? undefined,
    minOrderQty: row.min_order_qty ?? 1,
    packSize: row.pack_size ?? 1,
    weight: row.weight != null ? Number(row.weight) : undefined,
    weightUnit: row.weight_unit ?? undefined,
    isAvailable: row.is_available ?? true,
    inventoryQuantity: row.inventory_quantity ?? undefined,
  };
}

type CartRow = Parameters<typeof rowToItem>[0] & { cart_label: string | null };

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const activeCompanyId = useActiveCompanyId();
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCart, setActiveCart] = useState<string>(() => readActiveCart(activeCompanyId));
  const [cartQuantities, setCartQuantities] = useState<Record<string, number>>({});
  // Undgå at forældede svar (fx efter firmaskifte) overskriver nyere state
  const loadSeq = useRef(0);
  // Skelet vises kun før FØRSTE indlæsning — senere reloads (token-refresh,
  // vinduesfokus, kurveskift) sker i stilhed, så siden ikke blinker og åbne
  // dialoger (fx Kurve-vælgeren) ikke smides væk midt i brug.
  const hasLoadedRef = useRef(false);
  // Senest hentede rækker (alle firmaets kurve) til øjeblikkeligt kurveskift.
  const rowsRef = useRef<CartRow[]>([]);

  // Firmaskifte (kundevisning): læs firmaets egen aktive kurv + frisk skelet.
  useEffect(() => {
    hasLoadedRef.current = false;
    rowsRef.current = [];
    setIsLoading(true);
    setActiveCart(readActiveCart(activeCompanyId));
  }, [activeCompanyId]);

  // Token-refresh giver et nyt user-OBJEKT med samme id — genindlæs kun når
  // selve brugeren skifter.
  const userId = user?.id ?? null;

  const reload = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (!activeCompanyId || !userId) {
      setItems([]);
      setCartQuantities({});
      setIsLoading(false);
      return;
    }
    if (!hasLoadedRef.current) setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("cart_items")
        .select("*")
        .eq("company_id", activeCompanyId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      if (seq === loadSeq.current) {
        const rows = data ?? [];
        const quantities: Record<string, number> = {};
        for (const row of rows) {
          const label = (row.cart_label as string | null) || DEFAULT_CART;
          quantities[label] = (quantities[label] ?? 0) + Number(row.quantity ?? 0);
        }
        setCartQuantities(quantities);
        rowsRef.current = rows as CartRow[];
        hasLoadedRef.current = true;
        setItems(
          rows
            .filter((row) => ((row.cart_label as string | null) || DEFAULT_CART) === activeCart)
            .map(rowToItem),
        );
      }
    } catch (e) {
      logger.error("kurv_indlaesning_fejlede", { error: String(e), companyId: activeCompanyId });
      toast.error("Kurven kunne ikke indlæses. Prøv at genindlæse siden.");
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
  }, [activeCompanyId, userId, activeCart]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Kurveskift: vis straks den valgte kurvs varer fra de allerede hentede
  // rækker — reload() ovenfor henter friske data i baggrunden.
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    setItems(
      rowsRef.current
        .filter((row) => (row.cart_label || DEFAULT_CART) === activeCart)
        .map(rowToItem),
    );
  }, [activeCart]);

  const persistActiveCart = useCallback(
    (label: string) => {
      setActiveCart(label);
      if (!activeCompanyId) return;
      try {
        localStorage.setItem(activeCartStorageKey(activeCompanyId), label);
      } catch {
        // localStorage kan fejle (private mode) — kurven virker stadig for sessionen.
      }
    },
    [activeCompanyId],
  );

  const switchCart = useCallback(
    (label: string) => {
      const clean = label.trim();
      if (!clean || clean === activeCart) return;
      persistActiveCart(clean);
    },
    [activeCart, persistActiveCart],
  );

  const createCart = useCallback(
    (label: string) => {
      const clean = label.trim().slice(0, 40);
      if (!clean) return;
      persistActiveCart(clean);
      toast.success(`Kurven "${clean}" er oprettet`, {
        description: "Varer du lægger i kurven, lander her nu.",
      });
    },
    [persistActiveCart],
  );

  const deleteCart = useCallback(
    async (label: string) => {
      if (label === DEFAULT_CART || !activeCompanyId) return;
      setCartQuantities((prev) => {
        const next = { ...prev };
        delete next[label];
        return next;
      });
      if (label === activeCart) persistActiveCart(DEFAULT_CART);
      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("company_id", activeCompanyId)
        .eq("cart_label", label);
      if (error) {
        logger.error("kurv_slet_kurv_fejlede", { error: error.message, label });
        toast.error("Kurven kunne ikke slettes. Prøv igen.");
        void reload();
      }
    },
    [activeCompanyId, activeCart, persistActiveCart, reload],
  );

  const persistUpsert = useCallback(
    (item: CartItem) => {
      if (!activeCompanyId) return;
      void supabase
        .from("cart_items")
        .upsert(
          {
            company_id: activeCompanyId,
            cart_label: activeCart,
            variant_id: item.variantId,
            product_id: item.productId,
            title: item.title,
            variant_title: item.variantTitle ?? null,
            sku: item.sku ?? null,
            quantity: item.quantity,
            price: item.price,
            original_price: item.originalPrice,
            image: item.image ?? null,
            min_order_qty: item.minOrderQty,
            pack_size: item.packSize,
            weight: item.weight ?? null,
            weight_unit: item.weightUnit ?? null,
            is_available: item.isAvailable ?? true,
            inventory_quantity: item.inventoryQuantity ?? null,
          },
          { onConflict: "company_id,cart_label,variant_id" },
        )
        .then(({ error }) => {
          if (error) {
            logger.error("kurv_gem_fejlede", { error: error.message, variantId: item.variantId });
            toast.error("Ændringen kunne ikke gemmes. Kurven genindlæses.");
            void reload();
          }
        });
    },
    [activeCompanyId, activeCart, reload],
  );

  const persistDelete = useCallback(
    (variantId: string) => {
      if (!activeCompanyId) return;
      void supabase
        .from("cart_items")
        .delete()
        .eq("company_id", activeCompanyId)
        .eq("cart_label", activeCart)
        .eq("variant_id", variantId)
        .then(({ error }) => {
          if (error) {
            logger.error("kurv_slet_fejlede", { error: error.message, variantId });
            toast.error("Varen kunne ikke fjernes. Kurven genindlæses.");
            void reload();
          }
        });
    },
    [activeCompanyId, activeCart, reload],
  );

  const addItem = useCallback(
    (input: NewCartItem, quantity?: number) => {
      if (!activeCompanyId) {
        toast.error("Din konto mangler et firma — kontakt LAB3D.");
        return;
      }
      const rules = { packSize: input.packSize, minOrderQty: input.minOrderQty };
      setItems((prev) => {
        const existing = prev.find((i) => i.variantId === input.variantId);
        if (existing) {
          const updated = { ...existing, quantity: incrementQuantity(existing.quantity, rules) };
          persistUpsert(updated);
          return prev.map((i) => (i.variantId === input.variantId ? updated : i));
        }
        const qty =
          quantity != null ? normalizeQuantity(quantity, rules) : defaultAddQuantity(rules);
        const item: CartItem = {
          ...input,
          minOrderQty: input.minOrderQty ?? 1,
          packSize: input.packSize ?? 1,
          quantity: qty,
        };
        persistUpsert(item);
        return [...prev, item];
      });
    },
    [activeCompanyId, persistUpsert],
  );

  const addCustomItem = useCallback(
    (input: { title: string; priceExVat: number; quantity: number; sku?: string }) => {
      if (!activeCompanyId) return;
      const item: CartItem = {
        variantId: `${CUSTOM_VARIANT_PREFIX}${crypto.randomUUID()}`,
        productId: "custom",
        title: input.title,
        sku: input.sku,
        quantity: Math.max(1, Math.floor(input.quantity)),
        price: input.priceExVat,
        originalPrice: input.priceExVat,
        minOrderQty: 1,
        packSize: 1,
        weight: 0,
        isAvailable: true,
      };
      setItems((prev) => [...prev, item]);
      persistUpsert(item);
    },
    [activeCompanyId, persistUpsert],
  );

  const removeItem = useCallback(
    (variantId: string) => {
      setItems((prev) => prev.filter((i) => i.variantId !== variantId));
      persistDelete(variantId);
    },
    [persistDelete],
  );

  const updateQuantity = useCallback(
    (variantId: string, quantity: number) => {
      setItems((prev) => {
        const item = prev.find((i) => i.variantId === variantId);
        if (!item) return prev;
        // Under minimum → fjern linjen (som i dag)
        if (quantity < item.minOrderQty) {
          persistDelete(variantId);
          return prev.filter((i) => i.variantId !== variantId);
        }
        const normalized = normalizeQuantity(quantity, {
          packSize: item.packSize,
          minOrderQty: item.minOrderQty,
        });
        const updated = { ...item, quantity: normalized };
        persistUpsert(updated);
        return prev.map((i) => (i.variantId === variantId ? updated : i));
      });
    },
    [persistDelete, persistUpsert],
  );

  const updatePrice = useCallback(
    (variantId: string, newPriceExVat: number) => {
      if (!Number.isFinite(newPriceExVat) || newPriceExVat < 0) return;
      setItems((prev) =>
        prev.map((i) => {
          if (i.variantId !== variantId) return i;
          const updated = { ...i, price: newPriceExVat };
          persistUpsert(updated);
          return updated;
        }),
      );
    },
    [persistUpsert],
  );

  const clearCart = useCallback(async () => {
    setItems([]);
    setCartQuantities((prev) => ({ ...prev, [activeCart]: 0 }));
    if (!activeCompanyId) return;
    const { error } = await supabase
      .from("cart_items")
      .delete()
      .eq("company_id", activeCompanyId)
      .eq("cart_label", activeCart);
    if (error) {
      logger.error("kurv_toem_fejlede", { error: error.message, companyId: activeCompanyId });
    }
  }, [activeCompanyId, activeCart]);

  const totals = useMemo(() => {
    return {
      totalItems: items.reduce((sum, i) => sum + i.quantity, 0),
      subtotalExVat: items.reduce(
        (sum, i) => sum + lineTotalExVat({ priceExVat: i.price, quantity: i.quantity }),
        0,
      ),
      totalSavingsExVat: items.reduce(
        (sum, i) => sum + Math.max(0, i.originalPrice - i.price) * i.quantity,
        0,
      ),
      totalWeightKg: computeTotalWeightKg(items),
    };
  }, [items]);

  // Kurveliste: alle labels med varer + Standard + den aktive (også når tom).
  const carts = useMemo<CartSummary[]>(() => {
    const labels = new Set<string>([DEFAULT_CART, activeCart, ...Object.keys(cartQuantities)]);
    return [...labels]
      .sort((a, b) => (a === DEFAULT_CART ? -1 : b === DEFAULT_CART ? 1 : a.localeCompare(b, "da")))
      .map((label) => ({
        label,
        quantity: label === activeCart ? totals.totalItems : (cartQuantities[label] ?? 0),
      }));
  }, [cartQuantities, activeCart, totals.totalItems]);

  const value: CartState = useMemo(
    () => ({
      items,
      isLoading,
      ...totals,
      activeCart,
      carts,
      switchCart,
      createCart,
      deleteCart,
      addItem,
      addCustomItem,
      removeItem,
      updateQuantity,
      updatePrice,
      clearCart,
      reload,
    }),
    [items, isLoading, totals, activeCart, carts, switchCart, createCart, deleteCart, addItem, addCustomItem, removeItem, updateQuantity, updatePrice, clearCart, reload],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart skal bruges inden for CartProvider");
  return ctx;
}
